import React, { useEffect, useId, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  CircularProgress,
  Divider,
  Fade,
  FormControlLabel,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MarkerChips from "./MarkerChips";
import HelpTip from "./HelpTip";
import HELP from "./helpText";
import {
  flagLabel,
  railVerdict,
  reasonLines,
  relatedFlagLabel,
  resultsDiffer,
  signaturePrefix,
  verdictTone,
} from "./regmFormat";

import { AMBER_TEXT, GLAZER_GREEN } from "./regmColors";
import { TOUCH, focusRing } from "./regmStyles";

export { AMBER_TEXT, GLAZER_GREEN };

// Flagged and pending are amber, clean is Glazer green, unavailable and neutral are grey.
export const TONE_COLOR = {
  flagged: AMBER_TEXT,
  clean: GLAZER_GREEN,
  unavailable: "text.secondary",
  neutral: "text.secondary",
};

const greenButton = {
  bgcolor: GLAZER_GREEN,
  color: "#fff",
  minHeight: TOUCH,
  "&:hover": { bgcolor: GLAZER_GREEN },
  ...focusRing,
};

const Verdict = ({ result }) => {
  const verdict = railVerdict(result);
  return (
    <>
      <Typography
        component="p"
        sx={{ fontSize: "1.25rem", fontWeight: 700, lineHeight: 1.3, color: TONE_COLOR[verdict.tone] }}
      >
        {verdict.label}
      </Typography>
      {verdict.note && (
        <Typography variant="body2" sx={{ color: AMBER_TEXT }}>
          {verdict.note}
        </Typography>
      )}
    </>
  );
};

// Reasons, markers (all but "No") and the related verdict. Shown in the rail, or in the page body below md.
// Tooltips sit beside chips and the related line, never in the attestation text or its checkbox label.
export const VerdictDetails = ({ result, tipPlacement }) => {
  const reasons = reasonLines(result.flag_reasons);
  return (
    <Stack spacing={1}>
      {reasons.map((reason) => (
        <Typography key={reason} variant="body2">
          {reason}
        </Typography>
      ))}
      <MarkerChips source={result} hideNo help tipPlacement={tipPlacement} />
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="body2" sx={{ color: TONE_COLOR[verdictTone(result.related_flagged)] }}>
          {relatedFlagLabel(result.related_flagged)}
        </Typography>
        <HelpTip text={HELP.relatedInstruments} label="related instruments" placement={tipPlacement} />
      </Box>
    </Stack>
  );
};

export const AttestationText = ({ text, sx, id }) => (
  <Box id={id} role="region" tabIndex={0} aria-label="Attestation text" sx={{ whiteSpace: "pre-wrap", ...sx }}>
    <Typography variant="body2">{text}</Typography>
  </Box>
);

const AttestControls = ({ state, accepted, onAccept, onAttest, compact }) => (
  <Stack
    direction={compact ? { xs: "column", sm: "row" } : "column"}
    spacing={1}
    alignItems={compact ? { xs: "stretch", sm: "center" } : "stretch"}
  >
    <FormControlLabel
      sx={{ mr: 0, flex: 1 }}
      control={
        <Checkbox
          checked={accepted}
          onChange={(e) => onAccept(e.target.checked)}
          sx={focusRing}
          disabled={state === "attesting" || state === "unavailable"}
        />
      }
      label="I have reviewed the results above and attest"
    />
    <Button
      variant="contained"
      onClick={onAttest}
      disabled={state !== "ready"}
      startIcon={state === "attesting" ? <CircularProgress size={18} color="inherit" /> : null}
      sx={greenButton}
    >
      {state === "attesting" ? "Attesting..." : "Attest"}
    </Button>
  </Stack>
);

const UnavailableNote = () => (
  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
    This result cannot be attested. Run the check again.
  </Typography>
);

const Confirmation = ({ result, record, onDownload, onNewCheck, downloadError }) => {
  const headingRef = useRef(null);
  // Move focus to the confirmation so keyboard and screen reader users land on it.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);
  return (
    <Stack spacing={1.5} role="status">
      <Typography
        ref={headingRef}
        tabIndex={-1}
        component="h2"
        sx={{ fontSize: "1.25rem", fontWeight: 700, color: GLAZER_GREEN, outline: "none" }}
      >
        Attestation recorded
      </Typography>
      <Typography variant="body2">
        Record {record.id}, signature {signaturePrefix(record.signature)}
      </Typography>
      <Typography variant="body2">
        Stored result: {flagLabel(record.flagged)}; {relatedFlagLabel(record.related_flagged)}.
      </Typography>
      <MarkerChips source={record} />
      {resultsDiffer(result, record) && (
        <Alert severity="warning">
          The stored result differs from the one you reviewed. Stored: {flagLabel(record.flagged)};{" "}
          {relatedFlagLabel(record.related_flagged)}.
          {(record.flag_reasons || []).length > 0 ? ` Reasons: ${record.flag_reasons.join(" ")}` : ""}
        </Alert>
      )}
      <Button variant="contained" onClick={onDownload} sx={greenButton}>
        Download PDF
      </Button>
      {downloadError && <Alert severity="error">{downloadError}</Alert>}
      <Button variant="text" onClick={onNewCheck} sx={{ minHeight: TOUCH, color: "text.primary", ...focusRing }}>
        New check
      </Button>
    </Stack>
  );
};

// Bottom bar toggle: the full attestation text opens inside the bar (closed by default).
const ReadAttestation = ({ text }) => {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  return (
    <Box>
      <ButtonBase
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={bodyId}
        sx={{ minHeight: TOUCH, borderRadius: 1, ...focusRing }}
      >
        <ExpandMoreIcon
          fontSize="small"
          sx={{ mr: 1, transform: open ? "none" : "rotate(-90deg)", color: "text.secondary" }}
        />
        <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
          Read attestation
        </Typography>
      </ButtonBase>
      <Box id={bodyId} hidden={!open}>
        {open && <AttestationText text={text} sx={{ pt: 0.5, pb: 1 }} />}
      </Box>
    </Box>
  );
};

// The decision rail: verdict, reasons, attestation and the Attest control in one place.
// Wide screens: a sticky column; the middle (details and attestation) scrolls, the controls stay
// pinned at the bottom. Below md (compact): a sticky bottom bar with the verdict, a "Read attestation"
// toggle and the controls; the reasons are then rendered in the page body.
// Renders nothing for state "hidden" (no result, or a check in flight).
const DecisionRail = ({
  state,
  result,
  record,
  accepted,
  onAccept,
  onAttest,
  attestError,
  onDownload,
  downloadError,
  onNewCheck,
  compact,
}) => {
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)", { noSsr: true });
  if (state === "hidden" || !result) return null;
  const tone = state === "confirmed" ? GLAZER_GREEN : TONE_COLOR[railVerdict(result).tone];

  const frame = compact
    ? {
        position: "sticky",
        bottom: 0,
        zIndex: (theme) => theme.zIndex.appBar - 1,
        borderTop: "4px solid",
        borderTopColor: tone,
        bgcolor: "background.paper",
        boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.08)",
        maxHeight: "60vh",
        overflowY: "auto",
        px: 2,
        py: 1.5,
      }
    : {
        position: "sticky",
        top: 16,
        display: "flex",
        flexDirection: "column",
        maxHeight: "calc(100vh - 112px)",
        border: 1,
        borderColor: "divider",
        borderTop: "4px solid",
        borderTopColor: tone,
        borderRadius: 1,
        bgcolor: "background.paper",
        // Fallback when even the pinned parts do not fit: the whole rail scrolls, nothing is clipped.
        overflowY: "auto",
      };

  let body;
  if (state === "confirmed") {
    body = (
      <Fade in appear timeout={reducedMotion ? 0 : 200}>
        <Box sx={compact ? undefined : { p: 2 }}>
          <Confirmation
            result={result}
            record={record}
            onDownload={onDownload}
            onNewCheck={onNewCheck}
            downloadError={downloadError}
          />
        </Box>
      </Fade>
    );
  } else if (compact) {
    body = (
      <Stack spacing={1}>
        <Verdict result={result} />
        <ReadAttestation text={result.attestation_text} />
        {state === "unavailable" && <UnavailableNote />}
        <AttestControls state={state} accepted={accepted} onAccept={onAccept} onAttest={onAttest} compact />
        {attestError && <Alert severity="error">{attestError}</Alert>}
      </Stack>
    );
  } else {
    body = (
      <>
        <Box sx={{ flex: "1 1 auto", minHeight: 120, overflowY: "auto" }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Decision
            </Typography>
            <Verdict result={result} />
            <Box sx={{ mt: 1 }}>
              <VerdictDetails result={result} tipPlacement="left" />
            </Box>
          </Box>
          <Divider />
          <AttestationText text={result.attestation_text} sx={{ p: 2 }} />
        </Box>
        <Divider />
        <Box sx={{ p: 2, flexShrink: 0 }}>
          {state === "unavailable" && <UnavailableNote />}
          <AttestControls state={state} accepted={accepted} onAccept={onAccept} onAttest={onAttest} />
          {attestError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {attestError}
            </Alert>
          )}
        </Box>
      </>
    );
  }

  return (
    <Box component="aside" aria-label="Decision" sx={frame}>
      {body}
    </Box>
  );
};

export default DecisionRail;
